import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../lib/i18n';
import { blogDraftReviewHtml, blogDraftReviewOptions, blogDraftTitle, blogReviewKey, blogReviewMessage, blogReviewPageUrl } from '../../../lib/blog-draft-preview';
import { useBlogDraftPreview } from '../../../lib/blog-draft-preview-api';
import { closeBlogReview, openBlogReview, useBlogReviewFlow, type BlogReviewFlow, type BlogReviewSendResult } from '../../../lib/blog-review-store';
import { emailReviewDocument, reviewNonce } from '../../../lib/email-review';
import { useEmailReviewFlow } from '../../../lib/email-review-store';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useLoadBi } from '../../../lib/use-load-bi';
import { Button, Typography } from '../ui';
import { SiteReviewFrame, type ReviewDocument } from './SiteReviewFrame';
import styles from './SiteEditHost.module.css';
import own from './BlogReviewHost.module.css';

export function BlogReviewHost() {
  const flow = useBlogReviewFlow();
  const emailFlow = useEmailReviewFlow();
  useEffect(() => {
    if (emailFlow) closeBlogReview();
  }, [emailFlow]);
  if (!flow || flow.beside || typeof document === 'undefined') return null;
  return <BlogReviewSheet flow={flow} />;
}

export function BlogReviewPane({ flow }: { flow: BlogReviewFlow }) {
  return <BlogReview flow={flow} layout="pane" />;
}

function BlogReviewSheet({ flow }: { flow: BlogReviewFlow }) {
  const { t } = useTranslation();
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return createPortal(
    <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={flow.card.title || t('blogDraft.title')} data-testid="blogReviewSheet">
      <BlogReview flow={flow} layout="sheet" />
    </div>,
    document.body,
  );
}

function BlogReview({ flow, layout }: { flow: BlogReviewFlow; layout: 'pane' | 'sheet' }) {
  const { t } = useTranslation();
  const { card, workspaceId, conversationId } = flow;
  const [refused, setRefused] = useState<Exclude<BlogReviewSendResult, { status: 'sent' }> | null>(null);
  const phone = useIsPhone();
  const [measured, setMeasured] = useState(false);
  useEffect(() => setMeasured(true), []);
  const preview = useBlogDraftPreview(
    { workspaceId, metaSiteId: card.metaSiteId, draftPostId: card.draftPostId },
    phone ? 'mobile' : 'desktop',
    measured,
    flow.revision,
  );
  useLoadBi('blog-draft-review', { isLoading: preview.isLoading, isError: preview.isError });
  const pageUrl = blogReviewPageUrl(card.draftPostId);
  const title = preview.data ? blogDraftTitle(preview.data, card.title) : card.title;
  const label = title || t('blogDraft.title');
  const loading = t('blogDraft.loading');
  const unavailable = t('blogDraft.unavailable');
  const reviewDocument = useMemo<ReviewDocument | null>(
    () =>
      preview.data
        ? {
            srcDoc: emailReviewDocument(blogDraftReviewHtml(preview.data, title), pageUrl, reviewNonce(), blogDraftReviewOptions(preview.data)),
            label,
            loading,
            unavailable,
          }
        : null,
    [preview.data, title, pageUrl, label, loading, unavailable],
  );

  useEffect(() => {
    if (reviewDocument || layout !== 'sheet') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeBlogReview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reviewDocument, layout]);

  const finish = (message: string): string | null | false => {
    const result = flow.sendComments(message);
    if (result.status !== 'sent') {
      setRefused(result);
      return false;
    }
    closeBlogReview();
    return conversationId;
  };

  if (!reviewDocument) {
    return (
      <div className={layout === 'pane' ? own.paneStatus : styles.body} role="status" data-testid="blogReviewStatus">
        <Typography variant="p2" color="primary">
          {preview.isError ? unavailable : loading}
        </Typography>
        <Button variant="outlined" onClick={closeBlogReview}>
          {t('siteReview.close')}
        </Button>
      </div>
    );
  }
  const openNewer = (newer: Extract<BlogReviewSendResult, { status: 'newer' }>) => {
    setRefused(null);
    openBlogReview({ ...flow, card: newer.card, sendComments: newer.sendComments, revision: undefined });
    void preview.refetch();
  };

  return (
    <div className={own.review}>
      {refused && (
        <div className={own.notice} role="status" data-testid="blogReviewRefused">
          <Typography variant="p3" color="primary">
            {t(`blogDraft.refused.${refused.status}`)}
          </Typography>
          {refused.status === 'newer' && (
            <Button size="small" variant="outlined" onClick={() => openNewer(refused)} data-testid="blogReviewOpenNewer">
              {t('blogDraft.refused.openNewer')}
            </Button>
          )}
        </div>
      )}
      <SiteReviewFrame
        previewUrl={pageUrl}
        reviewKey={blogReviewKey(card.draftPostId, conversationId)}
        layout={layout}
        conversationId={conversationId}
        reviewDocument={reviewDocument}
        buildMessage={(comments) => blogReviewMessage({ ...card, title }, comments)}
        onReload={() => void preview.refetch()}
        onFinish={finish}
        onClose={closeBlogReview}
      />
    </div>
  );
}
